/**
 * Skills Intelligence™ — 40 evidence items.
 *
 * No psychometrics. No Likert. No virtue self-ratings. Each item asks about
 * something the learner has observably done, used or shipped, or puts them
 * in a situation where both answers are defensible so the choice reveals
 * something instead of rewarding the obvious answer.
 *
 * Weights: 1 = a hint, 2 = a real signal, 3 = did the thing end-to-end,
 * 4 = shipped it for someone else / under constraint.
 */

import type { EvidenceItem } from "./types";

export const EVIDENCE_ITEMS: EvidenceItem[] = [
  // ── digital (1–5) ──────────────────────────────────────────────────────
  {
    item_id: "SI-01", domain: "digital", kind: "have_done", multi: true,
    prompt: "Which of these have you actually done?",
    helper: "Pick every one that's true. Half-finished counts — you did it.",
    options: [
      { option_id: "a", label: "Built a website or landing page", weight: 3, skills: ["web_development", "digital_literacy"] },
      { option_id: "b", label: "Used an API to pull data into something", weight: 3, skills: ["api_integration", "programming"] },
      { option_id: "c", label: "Automated a task that used to be manual", weight: 3, skills: ["ai_automation", "workflow_design"] },
      { option_id: "d", label: "Cleaned or analysed a messy dataset", weight: 3, skills: ["data_analysis", "python_data"] },
      { option_id: "e", label: "Used Git or version control", weight: 2, skills: ["version_control"] },
      { option_id: "f", label: "Deployed something people could open on the internet", weight: 4, skills: ["cloud_deployment", "web_development"] },
      { option_id: "g", label: "None of these yet", weight: 0, skills: [] },
    ],
  },
  {
    item_id: "SI-02", domain: "digital", kind: "tools", multi: true,
    prompt: "Which of these have you used yourself — not just watched?",
    options: [
      { option_id: "a", label: "Excel or Google Sheets beyond simple sums", weight: 2, skills: ["spreadsheets"] },
      { option_id: "b", label: "Canva, Figma or similar", weight: 2, skills: ["visual_design"] },
      { option_id: "c", label: "A no-code builder (Wix, Webflow, Glide, Zapier)", weight: 2, skills: ["no_code_tools", "workflow_design"] },
      { option_id: "d", label: "VS Code, Colab or a terminal", weight: 3, skills: ["programming"] },
      { option_id: "e", label: "A database or SQL tool", weight: 3, skills: ["sql_queries"] },
      { option_id: "f", label: "A video editor", weight: 2, skills: ["video_editing"] },
    ],
  },
  {
    item_id: "SI-03", domain: "digital", kind: "artefact", multi: true,
    prompt: "Is any of it visible to someone else today?",
    helper: "A link, a file, a certificate. This is the strongest evidence there is.",
    options: [
      { option_id: "a", label: "A public repo", weight: 4, skills: ["version_control", "programming"] },
      { option_id: "b", label: "A live site or app", weight: 4, skills: ["web_development", "cloud_deployment"] },
      { option_id: "c", label: "A portfolio or profile", weight: 3, skills: ["content_creation", "digital_literacy"] },
      { option_id: "d", label: "A published post, video or newsletter", weight: 3, skills: ["content_creation", "writing"] },
      { option_id: "e", label: "A course certificate", weight: 2, skills: ["self_directed_learning"] },
      { option_id: "f", label: "Nothing public yet", weight: 0, skills: [] },
    ],
  },
  {
    item_id: "SI-04", domain: "digital", kind: "frequency", multi: false,
    prompt: "When a tool you rely on breaks, what usually happens next?",
    options: [
      { option_id: "a", label: "I fix it myself and move on", weight: 3, skills: ["testing_debugging", "self_directed_learning"] },
      { option_id: "b", label: "I search until I find someone who solved it", weight: 2, skills: ["research", "self_directed_learning"] },
      { option_id: "c", label: "I ask someone who knows and watch how they do it", weight: 2, skills: ["listening", "curiosity"] },
      { option_id: "d", label: "I work around it and keep going", weight: 1, skills: ["adaptability"] },
    ],
  },
  {
    item_id: "SI-05", domain: "digital", kind: "have_done", multi: true,
    prompt: "Which of these have you set up for yourself or someone else?",
    options: [
      { option_id: "a", label: "Two-factor authentication or a password manager", weight: 2, skills: ["cyber_hygiene"] },
      { option_id: "b", label: "A backup that actually restored something", weight: 3, skills: ["cyber_hygiene", "systems_thinking"] },
      { option_id: "c", label: "A shared drive or workspace others use", weight: 2, skills: ["digital_literacy", "collaboration"] },
      { option_id: "d", label: "A payment or UPI flow for a real transaction", weight: 3, skills: ["budgeting", "digital_literacy"] },
    ],
  },

  // ── data (6–10) ────────────────────────────────────────────────────────
  {
    item_id: "SI-06", domain: "data", kind: "have_done", multi: true,
    prompt: "With numbers, which have you done end-to-end?",
    options: [
      { option_id: "a", label: "Collected data yourself (survey, logs, field notes)", weight: 3, skills: ["research", "data_analysis"] },
      { option_id: "b", label: "Made a chart that changed someone's mind", weight: 4, skills: ["data_visualisation", "influence"] },
      { option_id: "c", label: "Calculated an average, trend or percentage change", weight: 2, skills: ["statistics"] },
      { option_id: "d", label: "Spotted an error in someone else's numbers", weight: 3, skills: ["data_analysis", "systems_thinking"] },
      { option_id: "e", label: "Wrote a query or a formula that others reused", weight: 4, skills: ["sql_queries", "spreadsheets"] },
    ],
  },
  {
    item_id: "SI-07", domain: "data", kind: "tools", multi: true,
    prompt: "Which of these have you written at least a few lines in?",
    options: [
      { option_id: "a", label: "Python", weight: 3, skills: ["python_data", "programming"] },
      { option_id: "b", label: "SQL", weight: 3, skills: ["sql_queries"] },
      { option_id: "c", label: "JavaScript", weight: 3, skills: ["programming", "web_development"] },
      { option_id: "d", label: "Pandas, NumPy or R", weight: 3, skills: ["python_data", "statistics"] },
      { option_id: "e", label: "Spreadsheet formulas with lookups or pivots", weight: 2, skills: ["spreadsheets"] },
      { option_id: "f", label: "None yet", weight: 0, skills: [] },
    ],
  },
  {
    item_id: "SI-08", domain: "data", kind: "scenario", multi: false,
    prompt: "Your team's report is due in an hour and two numbers don't agree.",
    options: [
      { option_id: "a", label: "Ship it with a note flagging the discrepancy", weight: 3, skills: ["decision_making", "ai_ethics"] },
      { option_id: "b", label: "Hold the report until you find which one is right", weight: 3, skills: ["data_analysis", "systems_thinking"] },
      { option_id: "c", label: "Use the number your manager already quoted publicly", weight: 1, skills: ["collaboration"] },
      { option_id: "d", label: "Cut the section entirely and deliver the rest", weight: 2, skills: ["decision_making", "project_management"] },
    ],
  },
  {
    item_id: "SI-09", domain: "data", kind: "have_done", multi: true,
    prompt: "Have you ever had to explain a number to someone who didn't want to hear it?",
    options: [
      { option_id: "a", label: "Yes — to a teacher, boss or client", weight: 4, skills: ["influence", "data_visualisation"] },
      { option_id: "b", label: "Yes — to family about money", weight: 3, skills: ["budgeting", "influence"] },
      { option_id: "c", label: "Yes — to a team that disagreed", weight: 4, skills: ["influence", "conflict_resolution"] },
      { option_id: "d", label: "Not yet", weight: 0, skills: [] },
    ],
  },
  {
    item_id: "SI-10", domain: "data", kind: "frequency", multi: false,
    prompt: "How often do you end up being the person who checks the maths?",
    options: [
      { option_id: "a", label: "Most times — people bring numbers to me", weight: 4, skills: ["data_analysis", "statistics"] },
      { option_id: "b", label: "Sometimes, if it's important", weight: 2, skills: ["data_analysis"] },
      { option_id: "c", label: "Rarely — I'd rather someone else did", weight: 0, skills: [] },
    ],
  },

  // ── ai (11–16) ─────────────────────────────────────────────────────────
  {
    item_id: "SI-11", domain: "ai", kind: "have_done", multi: true,
    prompt: "With AI tools, which have you actually done?",
    options: [
      { option_id: "a", label: "Rewrote a prompt because the first answer was weak", weight: 3, skills: ["prompting"] },
      { option_id: "b", label: "Used AI to draft something you then heavily edited", weight: 2, skills: ["ai_tool_usage", "writing"] },
      { option_id: "c", label: "Built something with an AI API or agent", weight: 4, skills: ["ai_automation", "api_integration"] },
      { option_id: "d", label: "Caught an AI being confidently wrong", weight: 3, skills: ["ai_ethics", "systems_thinking"] },
      { option_id: "e", label: "Chained several tools into one workflow", weight: 4, skills: ["workflow_design", "ai_automation"] },
      { option_id: "f", label: "Trained or fine-tuned a model", weight: 4, skills: ["ml_foundations"] },
      { option_id: "g", label: "Not yet", weight: 0, skills: [] },
    ],
  },
  {
    item_id: "SI-12", domain: "ai", kind: "tools", multi: true,
    prompt: "Which AI tools have you used more than twice?",
    options: [
      { option_id: "a", label: "A chat assistant (ChatGPT, Gemini, Claude)", weight: 2, skills: ["ai_tool_usage"] },
      { option_id: "b", label: "An image or video generator", weight: 2, skills: ["ai_tool_usage", "visual_design"] },
      { option_id: "c", label: "A coding assistant", weight: 3, skills: ["ai_tool_usage", "programming"] },
      { option_id: "d", label: "An automation platform (Zapier, Make, n8n)", weight: 3, skills: ["ai_automation", "workflow_design"] },
      { option_id: "e", label: "A transcription, research or notes tool", weight: 2, skills: ["ai_tool_usage", "research"] },
    ],
  },
  {
    item_id: "SI-13", domain: "ai", kind: "scenario", multi: false,
    prompt: "An AI writes your assignment better than you could. What do you do?",
    helper: "There's no trick here — people genuinely differ.",
    options: [
      { option_id: "a", label: "Submit it and say you used AI", weight: 3, skills: ["ai_ethics"] },
      { option_id: "b", label: "Rewrite it in your own words using its structure", weight: 3, skills: ["ai_ethics", "writing"] },
      { option_id: "c", label: "Keep it as a benchmark and write your own from scratch", weight: 3, skills: ["self_directed_learning", "reflection"] },
      { option_id: "d", label: "Use it and say nothing", weight: 0, skills: [] },
    ],
  },
  {
    item_id: "SI-14", domain: "ai", kind: "have_done", multi: true,
    prompt: "Which of these has AI saved you real time on?",
    options: [
      { option_id: "a", label: "Studying or revising", weight: 2, skills: ["self_directed_learning"] },
      { option_id: "b", label: "Writing emails, applications or posts", weight: 2, skills: ["writing"] },
      { option_id: "c", label: "Debugging or writing code", weight: 3, skills: ["testing_debugging", "programming"] },
      { option_id: "d", label: "Repetitive admin someone used to pay for", weight: 4, skills: ["ai_automation"] },
      { option_id: "e", label: "Designing or editing media", weight: 2, skills: ["visual_design", "video_editing"] },
    ],
  },
  {
    item_id: "SI-15", domain: "ai", kind: "frequency", multi: false,
    prompt: "When did you last learn a tool that didn't exist two years ago?",
    options: [
      { option_id: "a", label: "This month", weight: 4, skills: ["curiosity", "adaptability", "ai_tool_usage"] },
      { option_id: "b", label: "In the last six months", weight: 3, skills: ["curiosity", "adaptability"] },
      { option_id: "c", label: "Over a year ago", weight: 1, skills: [] },
      { option_id: "d", label: "Can't remember one", weight: 0, skills: [] },
    ],
  },
  {
    item_id: "SI-16", domain: "ai", kind: "have_done", multi: true,
    prompt: "Have you ever designed how work should flow, not just done the work?",
    options: [
      { option_id: "a", label: "Wrote down the steps so someone else could repeat it", weight: 3, skills: ["workflow_design", "systems_thinking"] },
      { option_id: "b", label: "Removed a step that wasn't needed", weight: 3, skills: ["systems_thinking"] },
      { option_id: "c", label: "Built a template or checklist others now use", weight: 4, skills: ["workflow_design", "project_management"] },
      { option_id: "d", label: "Not yet", weight: 0, skills: [] },
    ],
  },

  // ── engineering / making (17–20) ───────────────────────────────────────
  {
    item_id: "SI-17", domain: "engineering", kind: "have_done", multi: true,
    prompt: "Which have you built with your hands or with code?",
    options: [
      { option_id: "a", label: "A working prototype, model or rig", weight: 3, skills: ["making_hardware"] },
      { option_id: "b", label: "Fixed a machine, bike or appliance", weight: 3, skills: ["making_hardware", "testing_debugging"] },
      { option_id: "c", label: "A program that someone else ran", weight: 4, skills: ["programming"] },
      { option_id: "d", label: "A circuit, robot or IoT thing", weight: 3, skills: ["making_hardware", "systems_thinking"] },
    ],
  },
  {
    item_id: "SI-18", domain: "engineering", kind: "scenario", multi: false,
    prompt: "Something you built stops working the day after you hand it over.",
    options: [
      { option_id: "a", label: "Reproduce the failure before touching anything", weight: 4, skills: ["testing_debugging", "systems_thinking"] },
      { option_id: "b", label: "Roll it back to what worked, then investigate", weight: 3, skills: ["decision_making", "testing_debugging"] },
      { option_id: "c", label: "Ask them exactly what they clicked", weight: 3, skills: ["listening", "testing_debugging"] },
      { option_id: "d", label: "Rebuild the broken part from scratch", weight: 2, skills: ["making_hardware"] },
    ],
  },
  {
    item_id: "SI-19", domain: "engineering", kind: "have_done", multi: true,
    prompt: "Have you worked inside a real constraint?",
    helper: "Constraints are where skill shows.",
    options: [
      { option_id: "a", label: "A deadline you couldn't move", weight: 3, skills: ["project_management", "decision_making"] },
      { option_id: "b", label: "A budget of almost nothing", weight: 3, skills: ["budgeting", "entrepreneurship"] },
      { option_id: "c", label: "Bad internet, shared device or power cuts", weight: 3, skills: ["adaptability"] },
      { option_id: "d", label: "A client who changed their mind late", weight: 3, skills: ["customer_service", "adaptability"] },
    ],
  },
  {
    item_id: "SI-20", domain: "engineering", kind: "artefact", multi: false,
    prompt: "What's the biggest thing you've finished, start to finish?",
    helper: "Describe it in a line — we map it to open-standard skills.",
    options: [
      { option_id: "a", label: "Something used by strangers", weight: 4, skills: ["programming", "project_management", "initiative"] },
      { option_id: "b", label: "Something used by my class, team or family", weight: 3, skills: ["project_management", "collaboration"] },
      { option_id: "c", label: "Something only I used", weight: 2, skills: ["self_directed_learning"] },
      { option_id: "d", label: "Haven't finished one yet", weight: 0, skills: [] },
    ],
  },

  // ── design & communication (21–26) ─────────────────────────────────────
  {
    item_id: "SI-21", domain: "design", kind: "have_done", multi: true,
    prompt: "Which of these have you made for a real audience?",
    options: [
      { option_id: "a", label: "A poster, deck or brand look", weight: 3, skills: ["visual_design"] },
      { option_id: "b", label: "A video or reel", weight: 3, skills: ["video_editing", "content_creation"] },
      { option_id: "c", label: "A written piece someone published", weight: 4, skills: ["writing", "content_creation"] },
      { option_id: "d", label: "Interviewed users before designing", weight: 4, skills: ["ux_research", "listening"] },
    ],
  },
  {
    item_id: "SI-22", domain: "communication", kind: "have_done", multi: true,
    prompt: "Where have you spoken and been listened to?",
    options: [
      { option_id: "a", label: "In front of a class or assembly", weight: 3, skills: ["public_speaking"] },
      { option_id: "b", label: "To adults who could say no", weight: 4, skills: ["public_speaking", "influence"] },
      { option_id: "c", label: "On camera or a live stream", weight: 3, skills: ["public_speaking", "content_creation"] },
      { option_id: "d", label: "Translating for someone who couldn't follow", weight: 3, skills: ["multilingual", "listening"] },
      { option_id: "e", label: "Not yet", weight: 0, skills: [] },
    ],
  },
  {
    item_id: "SI-23", domain: "communication", kind: "scenario", multi: false,
    prompt: "A teammate's work isn't good enough and they're proud of it.",
    options: [
      { option_id: "a", label: "Say it plainly, in private, with one fix", weight: 4, skills: ["conflict_resolution", "mentoring"] },
      { option_id: "b", label: "Ask them what they'd change if they had a day more", weight: 4, skills: ["mentoring", "listening"] },
      { option_id: "c", label: "Fix it yourself and don't mention it", weight: 1, skills: ["initiative"] },
      { option_id: "d", label: "Raise it with the group so it isn't personal", weight: 2, skills: ["facilitation"] },
    ],
  },
  {
    item_id: "SI-24", domain: "communication", kind: "frequency", multi: false,
    prompt: "How many languages do you work in comfortably?",
    options: [
      { option_id: "a", label: "One", weight: 1, skills: [] },
      { option_id: "b", label: "Two", weight: 3, skills: ["multilingual"] },
      { option_id: "c", label: "Three or more", weight: 4, skills: ["multilingual", "adaptability"] },
    ],
  },
  {
    item_id: "SI-25", domain: "communication", kind: "have_done", multi: true,
    prompt: "Have you ever written something that made something happen?",
    options: [
      { option_id: "a", label: "An application that got accepted", weight: 3, skills: ["writing"] },
      { option_id: "b", label: "A message that got a stranger to reply", weight: 3, skills: ["writing", "influence"] },
      { option_id: "c", label: "A complaint that got resolved", weight: 3, skills: ["writing", "conflict_resolution"] },
      { option_id: "d", label: "A story people shared", weight: 4, skills: ["storytelling", "content_creation"] },
    ],
  },
  {
    item_id: "SI-26", domain: "communication", kind: "scenario", multi: false,
    prompt: "You're in a meeting where you're the least experienced person and you spot a mistake.",
    options: [
      { option_id: "a", label: "Say it in the room", weight: 4, skills: ["initiative", "public_speaking"] },
      { option_id: "b", label: "Message the person who made it, after", weight: 3, skills: ["listening", "conflict_resolution"] },
      { option_id: "c", label: "Ask a question that leads them to it", weight: 4, skills: ["influence", "facilitation"] },
      { option_id: "d", label: "Wait and see if someone else catches it", weight: 1, skills: [] },
    ],
  },

  // ── leadership (27–32) ─────────────────────────────────────────────────
  {
    item_id: "SI-27", domain: "leadership", kind: "have_done", multi: true,
    prompt: "Which of these have you been responsible for?",
    options: [
      { option_id: "a", label: "A group that had to deliver something", weight: 4, skills: ["team_leadership", "project_management"] },
      { option_id: "b", label: "Money that wasn't yours", weight: 4, skills: ["budgeting"] },
      { option_id: "c", label: "A younger sibling, student or junior", weight: 3, skills: ["mentoring", "caregiving"] },
      { option_id: "d", label: "An event that had to happen on a date", weight: 4, skills: ["project_management", "community_organising"] },
      { option_id: "e", label: "Nothing like this yet", weight: 0, skills: [] },
    ],
  },
  {
    item_id: "SI-28", domain: "leadership", kind: "scenario", multi: false,
    prompt: "Half your team stops replying two days before the deadline.",
    options: [
      { option_id: "a", label: "Cut scope and ship what the remaining people can", weight: 4, skills: ["decision_making", "project_management"] },
      { option_id: "b", label: "Call each of them and find out what's going on", weight: 4, skills: ["team_leadership", "listening"] },
      { option_id: "c", label: "Do their parts yourself overnight", weight: 2, skills: ["initiative"] },
      { option_id: "d", label: "Tell whoever's in charge immediately", weight: 3, skills: ["decision_making"] },
    ],
  },
  {
    item_id: "SI-29", domain: "leadership", kind: "have_done", multi: true,
    prompt: "Have you ever handed work to someone else?",
    options: [
      { option_id: "a", label: "Yes, and it came back done", weight: 4, skills: ["delegation", "team_leadership"] },
      { option_id: "b", label: "Yes, and I had to redo it", weight: 2, skills: ["delegation"] },
      { option_id: "c", label: "I prefer doing it myself", weight: 0, skills: [] },
    ],
  },
  {
    item_id: "SI-30", domain: "leadership", kind: "have_done", multi: true,
    prompt: "Which of these have you started when nobody asked you to?",
    options: [
      { option_id: "a", label: "A club, group or community", weight: 4, skills: ["initiative", "community_organising"] },
      { option_id: "b", label: "A side project or small business", weight: 4, skills: ["entrepreneurship", "initiative"] },
      { option_id: "c", label: "A campaign about something you care about", weight: 4, skills: ["community_organising", "influence"] },
      { option_id: "d", label: "A study group or peer tutoring", weight: 3, skills: ["teaching", "initiative"] },
    ],
  },
  {
    item_id: "SI-31", domain: "leadership", kind: "scenario", multi: false,
    prompt: "Two people you work with can't stand each other and it's slowing everything down.",
    options: [
      { option_id: "a", label: "Get them in one room and name the problem", weight: 4, skills: ["conflict_resolution", "facilitation"] },
      { option_id: "b", label: "Split the work so they never overlap", weight: 3, skills: ["project_management", "decision_making"] },
      { option_id: "c", label: "Talk to each of them separately first", weight: 4, skills: ["conflict_resolution", "listening"] },
      { option_id: "d", label: "Focus on the work and let it pass", weight: 1, skills: [] },
    ],
  },
  {
    item_id: "SI-32", domain: "leadership", kind: "frequency", multi: false,
    prompt: "When a group has no plan, what usually happens?",
    options: [
      { option_id: "a", label: "I end up making one", weight: 4, skills: ["team_leadership", "initiative"] },
      { option_id: "b", label: "I ask questions until one appears", weight: 3, skills: ["facilitation", "curiosity"] },
      { option_id: "c", label: "I wait for someone to lead, then back them fully", weight: 2, skills: ["collaboration"] },
      { option_id: "d", label: "I do my own part well and let the rest sort itself", weight: 1, skills: [] },
    ],
  },

  // ── enterprise & care (33–36) ──────────────────────────────────────────
  {
    item_id: "SI-33", domain: "enterprise", kind: "have_done", multi: true,
    prompt: "Have you ever been paid, or earned something, for work you did?",
    options: [
      { option_id: "a", label: "Freelance or gig work", weight: 4, skills: ["entrepreneurship", "customer_service"] },
      { option_id: "b", label: "Sold something you made or sourced", weight: 4, skills: ["selling", "entrepreneurship"] },
      { option_id: "c", label: "Tutored or coached someone", weight: 3, skills: ["teaching", "mentoring"] },
      { option_id: "d", label: "An internship or part-time job", weight: 3, skills: ["customer_service", "collaboration"] },
      { option_id: "e", label: "A prize, grant or scholarship for work you submitted", weight: 3, skills: ["writing", "initiative"] },
      { option_id: "f", label: "Not yet", weight: 0, skills: [] },
    ],
  },
  {
    item_id: "SI-34", domain: "enterprise", kind: "have_done", multi: true,
    prompt: "Which of these have you handled for real?",
    options: [
      { option_id: "a", label: "An angry customer or user", weight: 4, skills: ["customer_service", "conflict_resolution"] },
      { option_id: "b", label: "A budget you had to keep to", weight: 3, skills: ["budgeting"] },
      { option_id: "c", label: "Promoting something so strangers turned up", weight: 4, skills: ["marketing", "influence"] },
      { option_id: "d", label: "Negotiating a price", weight: 3, skills: ["selling", "influence"] },
    ],
  },
  {
    item_id: "SI-35", domain: "care", kind: "have_done", multi: true,
    prompt: "Who has depended on you?",
    helper: "Care work is skilled work. It counts here.",
    options: [
      { option_id: "a", label: "A family member who needed daily help", weight: 4, skills: ["caregiving", "reflection"] },
      { option_id: "b", label: "Someone learning from me", weight: 3, skills: ["teaching", "mentoring"] },
      { option_id: "c", label: "A group I kept together", weight: 3, skills: ["facilitation", "community_organising"] },
      { option_id: "d", label: "Someone in a bad moment", weight: 3, skills: ["listening", "caregiving"] },
    ],
  },
  {
    item_id: "SI-36", domain: "care", kind: "scenario", multi: false,
    prompt: "You've explained something twice and they still don't get it.",
    options: [
      { option_id: "a", label: "Find a different example from their world", weight: 4, skills: ["teaching", "storytelling"] },
      { option_id: "b", label: "Ask them to explain it back to you", weight: 4, skills: ["teaching", "listening"] },
      { option_id: "c", label: "Show them instead of telling them", weight: 3, skills: ["teaching", "facilitation"] },
      { option_id: "d", label: "Leave it and come back tomorrow", weight: 2, skills: ["reflection"] },
    ],
  },

  // ── learning velocity (37–40) ──────────────────────────────────────────
  {
    item_id: "SI-37", domain: "learning", kind: "have_done", multi: true,
    prompt: "How have you learned the last useful thing you learned?",
    options: [
      { option_id: "a", label: "Taught myself from free material", weight: 3, skills: ["self_directed_learning", "curiosity"] },
      { option_id: "b", label: "Finished an online course", weight: 3, skills: ["self_directed_learning"] },
      { option_id: "c", label: "Copied someone better than me, closely", weight: 3, skills: ["curiosity", "reflection"] },
      { option_id: "d", label: "Broke something and had to fix it", weight: 4, skills: ["testing_debugging", "adaptability"] },
      { option_id: "e", label: "In a classroom", weight: 2, skills: [] },
    ],
  },
  {
    item_id: "SI-38", domain: "learning", kind: "frequency", multi: false,
    prompt: "How long from wanting to learn something to actually starting?",
    options: [
      { option_id: "a", label: "Same day", weight: 4, skills: ["initiative", "self_directed_learning"] },
      { option_id: "b", label: "Within a week", weight: 3, skills: ["self_directed_learning"] },
      { option_id: "c", label: "Within a few months", weight: 1, skills: [] },
      { option_id: "d", label: "There's a list I haven't started", weight: 0, skills: [] },
    ],
  },
  {
    item_id: "SI-39", domain: "learning", kind: "scenario", multi: false,
    prompt: "The field you're aiming at changes shape in three years.",
    options: [
      { option_id: "a", label: "Go deeper — the fundamentals still hold", weight: 3, skills: ["systems_thinking", "self_directed_learning"] },
      { option_id: "b", label: "Go wider — pick up the adjacent thing early", weight: 3, skills: ["adaptability", "curiosity"] },
      { option_id: "c", label: "Follow the people who see it coming", weight: 3, skills: ["research", "curiosity"] },
      { option_id: "d", label: "Build something with it before anyone agrees it matters", weight: 4, skills: ["entrepreneurship", "initiative"] },
    ],
  },
  {
    item_id: "SI-40", domain: "learning", kind: "have_done", multi: true,
    prompt: "Which of these have you done in the last three months?",
    options: [
      { option_id: "a", label: "Asked for feedback and changed something because of it", weight: 4, skills: ["reflection", "mentoring"] },
      { option_id: "b", label: "Read or watched something outside my subject", weight: 2, skills: ["curiosity"] },
      { option_id: "c", label: "Wrote down what I learned", weight: 3, skills: ["reflection", "writing"] },
      { option_id: "d", label: "Taught someone else what I'd just learned", weight: 4, skills: ["teaching", "reflection"] },
      { option_id: "e", label: "None of these", weight: 0, skills: [] },
    ],
  },
];

export const EVIDENCE_ITEM_COUNT = EVIDENCE_ITEMS.length;

export function evidenceItem(item_id: string): EvidenceItem | undefined {
  return EVIDENCE_ITEMS.find((i) => i.item_id === item_id);
}
